// components/token/TokenInfo.js
import React, { useState, useEffect, useRef } from "react";
import { useIdleDetection } from "../../hooks/useIdleDetection";
import { getTokenPairs, extractMarketData, getComprehensivePairData } from "../../services/dexscreener-api";
import TradingControls from "./TradingControls";

const API_KEY = process.env.REACT_APP_MORALIS_API_KEY;

// Feature flag to use DexScreener for all TokenInfo data
const USE_DEXSCREENER_TOKENINFO = process.env.REACT_APP_USE_DEXSCREENER_TOKENINFO === "true";

// Debug log to verify feature flag is loaded correctly
console.log("🔍 [TokenInfo] USE_DEXSCREENER_TOKENINFO:", {
  envVar: process.env.REACT_APP_USE_DEXSCREENER_TOKENINFO,
  enabled: USE_DEXSCREENER_TOKENINFO
});

const TokenInfo = ({ token, pair, timeFrame, chainId, onPairStatsUpdate }) => {
  const [pairStats, setPairStats] = useState(null);
  const [tokenMetadata, setTokenMetadata] = useState(null);
  const [marketData, setMarketData] = useState(null); // DexScreener market data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTimeFrame, setSelectedTimeFrame] = useState("24h");
  const containerRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isSolana, setIsSolana] = useState(false);
  
  // Idle detection - pause API calls when user inactive
  const isIdle = useIdleDetection(60000); // 1 minute idle time

  // Block explorer URLs by chain
  const blockExplorers = {
    "0x1": "https://etherscan.io",
    "0x38": "https://bscscan.com",
    "0x89": "https://polygonscan.com",
    "0xa4b1": "https://arbiscan.io",
    "0xa": "https://optimistic.etherscan.io",
    "0x2105": "https://basescan.org",
    "0xa86a": "https://snowtrace.io",
    "0xe708": "https://lineascan.build",
    "0xfa": "https://ftmscan.com",
    "0x171": "https://scan.pulsechain.com",
    "0x7e4": "https://app.roninchain.com",
    solana: "https://solscan.io",
  };

  // Map UI timeframes to API timeframes
  const timeFrameMap = {
    "5m": "5min",
    "1h": "1h",
    "24h": "24h",
  };

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        setScrollPosition(containerRef.current.scrollTop);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  // Fetch token metadata - refresh periodically to catch "graduated" tokens
  useEffect(() => {
    if (!token || !token.address) return;

    // If feature flag is enabled, completely skip this useEffect (metadata comes from DexScreener via pair stats)
    if (USE_DEXSCREENER_TOKENINFO) {
      setIsSolana(chainId === "solana");
      return; // Exit early - no Moralis calls at all
    }

    const fetchTokenMetadata = async () => {
      try {
        let url;
        const isSolana = chainId === "solana";
        setIsSolana(isSolana);

        if (isSolana) {
          url = `https://solana-gateway.moralis.io/token/mainnet/${token.address}/metadata`;
        } else {
          url = `https://deep-index.moralis.io/api/v2.2/erc20/metadata?chain=${chainId}&addresses[0]=${token.address}`;
        }

        console.log("Fetching token metadata from:", url);

        const response = await fetch(url, {
          headers: {
            accept: "application/json",
            "X-API-Key": API_KEY,
          },
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        console.log("Token metadata response:", data);

        // Handle different response formats
        if (isSolana) {
          setTokenMetadata(data);
        } else {
          // EVM response is an array, take first item
          setTokenMetadata(
            Array.isArray(data) && data.length > 0 ? data[0] : null
          );
        }
      } catch (err) {
        // Only log errors if not using DexScreener (to avoid spamming console with expected errors)
        if (!USE_DEXSCREENER_TOKENINFO) {
          console.error("Error fetching token metadata:", err);
        }
      }
    };

    // Initial fetch (only if feature flag is disabled)
    fetchTokenMetadata();

    // Refresh metadata every 2 minutes (120 seconds) to catch token "graduations"
    const metadataInterval = setInterval(fetchTokenMetadata, 120000);

    return () => {
      clearInterval(metadataInterval);
    };
  }, [token, chainId, pair, USE_DEXSCREENER_TOKENINFO]);

  // Fetch market data from DexScreener (market cap, volume) - every 10 seconds when user active
  // Note: If USE_DEXSCREENER_TOKENINFO is enabled, market data is fetched in fetchPairStats
  useEffect(() => {
    if (!token || !token.address || isIdle) {
      // Don't fetch if idle or no token
      return;
    }
    
    // Skip if using comprehensive DexScreener data (handled in fetchPairStats)
    if (USE_DEXSCREENER_TOKENINFO && pair?.pairAddress) {
      return;
    }

    const fetchMarketData = async () => {
      try {
        // Convert chainId to DexScreener format
        const dexScreenerChainId = chainId === "solana" ? "solana" : 
          chainId === "0x1" ? "ethereum" :
          chainId === "0x38" ? "bsc" :
          chainId === "0x89" ? "polygon" :
          chainId === "0xa4b1" ? "arbitrum" :
          chainId === "0x2105" ? "base" :
          chainId === "0xa" ? "optimism" :
          chainId === "0xa86a" ? "avalanche" :
          chainId; // fallback to original

        const pairs = await getTokenPairs(dexScreenerChainId, token.address);
        const extracted = extractMarketData(pairs);
        
        console.log("📊 DexScreener market data:", extracted);
        setMarketData(extracted);
        
        // Update parent with market data if pair exists
        if (onPairStatsUpdate && pair && pairs.length > 0) {
          const bestPair = pairs[0]; // Use first pair
          onPairStatsUpdate({
            ...pair,
            usdPrice: bestPair.priceUsd || pair.usdPrice,
            volume24hrUsd: extracted.volume24h || pair.volume24hrUsd,
            liquidityUsd: extracted.liquidity || pair.liquidityUsd,
            marketCap: extracted.marketCap || pair.marketCap,
          });
        }
      } catch (err) {
        console.error("Error fetching DexScreener market data:", err);
        // Don't set error - just log it, fallback to existing data
      }
    };

    // Initial fetch
    fetchMarketData();

    // Poll every 10 seconds when user is active (as per requirement)
    const marketDataInterval = setInterval(() => {
      if (!isIdle) {
        fetchMarketData();
      }
    }, 10000);

    return () => {
      clearInterval(marketDataInterval);
    };
  }, [token, chainId, isIdle, pair, onPairStatsUpdate, USE_DEXSCREENER_TOKENINFO]);

  // Fetch pair stats - every 10 seconds when active (default behavior)
  useEffect(() => {
    if (!pair || !pair.pairAddress || isIdle) return;

    const fetchPairStats = async () => {
      try {
        // Use DexScreener if flag is enabled
        if (USE_DEXSCREENER_TOKENINFO) {
          console.log("📊 [TokenInfo] Fetching comprehensive data from DexScreener...", { chainId, pairAddress: pair.pairAddress });
          const comprehensiveData = await getComprehensivePairData(chainId, pair.pairAddress);
          console.log("📊 [TokenInfo] Comprehensive data received:", { 
            hasData: !!comprehensiveData,
            hasPairStats: !!comprehensiveData?.pairStats,
            pairStatsKeys: comprehensiveData?.pairStats ? Object.keys(comprehensiveData.pairStats) : null,
            pricePercentChange: comprehensiveData?.pairStats?.pricePercentChange,
          });
          
          if (comprehensiveData?.pairStats) {
            setPairStats(comprehensiveData.pairStats);
            
            // Also update market data from comprehensive response
            if (comprehensiveData.marketData) {
              setMarketData(comprehensiveData.marketData);
            }
            
            // Update parent with comprehensive data including pairLabel and exchangeName
            if (onPairStatsUpdate && pair) {
              onPairStatsUpdate({
                ...pair,
                // Update pairLabel and exchangeName from DexScreener
                pairLabel: comprehensiveData.pairInfo?.pairLabel || pair.pairLabel,
                exchangeName: comprehensiveData.pairInfo?.exchangeName || pair.exchangeName,
                // Update price change
                usdPrice24hrPercentChange: comprehensiveData.pairStats.pricePercentChange?.['24h'] || pair.usdPrice24hrPercentChange,
                // Update prices
                usdPrice: comprehensiveData.pairStats.currentUsdPrice || pair.usdPrice,
                nativePrice: comprehensiveData.pairStats.currentNativePrice || pair.nativePrice,
                // Update liquidity
                liquidityUsd: comprehensiveData.pairStats.totalLiquidityUsd || pair.liquidityUsd,
                // Update volume
                volume24hrUsd: comprehensiveData.pairStats.totalVolume?.['24h'] || pair.volume24hrUsd,
              });
            }
            return;
          } else {
            console.warn("⚠️ [TokenInfo] Comprehensive data missing pairStats:", comprehensiveData);
          }
        }

        // Fallback to Moralis
        let url;
        const isSolana = chainId === "solana";

        if (isSolana) {
          url = `https://solana-gateway.moralis.io/token/mainnet/pairs/${pair.pairAddress}/stats`;
        } else {
          url = `https://deep-index.moralis.io/api/v2.2/pairs/${pair.pairAddress}/stats?chain=${chainId}`;
        }

        const response = await fetch(url, {
          headers: {
            accept: "application/json",
            "X-API-Key": API_KEY,
          },
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        setPairStats(data);
        
        // Update parent with price change data
        if (onPairStatsUpdate && pair) {
          onPairStatsUpdate({
            ...pair,
            usdPrice24hrPercentChange: data.priceChange24h || data.price_change_24h || pair.usdPrice24hrPercentChange,
          });
        }
      } catch (err) {
        console.error("Error fetching pair stats:", err);
        // Don't set error - just log it
      }
    };

    // Initial fetch
    fetchPairStats();

    // Poll every 10 seconds (default behavior - no slow edits for price updates)
    const statsInterval = setInterval(() => {
      if (!isIdle) {
        fetchPairStats();
      }
    }, 10000);

    return () => {
      clearInterval(statsInterval);
    };
  }, [pair, chainId, isIdle, onPairStatsUpdate, USE_DEXSCREENER_TOKENINFO]);

  // Handle time frame selection
  const handleTimeFrameChange = (timeFrame) => {
    setSelectedTimeFrame(timeFrame);
  };

  // Format price with appropriate decimal places
  const formatPrice = (price) => {
    if (!price) return "$0";

    // Convert to number if it's a string
    const numPrice = typeof price === "string" ? parseFloat(price) : price;

    if (numPrice < 0.00001) {
      return "$" + numPrice.toFixed(10).replace(/\.?0+$/, "");
    } else if (numPrice < 0.01) {
      return "$" + numPrice.toFixed(6);
    } else if (numPrice < 1000) {
      return "$" + numPrice.toFixed(4);
    } else {
      return (
        "$" +
        numPrice.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    }
  };

  // Format token price in terms of the quote token
  const formatTokenPrice = (price, symbol) => {
    if (!price) return "0";

    const numPrice = typeof price === "string" ? parseFloat(price) : price;

    if (numPrice < 0.00001) {
      return `${numPrice.toFixed(10).replace(/\.?0+$/, "")} ${symbol}`;
    } else {
      const parts = numPrice.toString().split(".");
      if (parts.length > 1) {
        // Take 6 significant digits after the decimal
        return `${parts[0]}.${parts[1].substring(0, 6)} ${symbol}`;
      }
      return `${numPrice} ${symbol}`;
    }
  };

  // Format large numbers with K, M, B suffixes
  const formatNumber = (num) => {
    if (!num) return "0";

    const numValue = typeof num === "string" ? parseFloat(num) : num;

    if (numValue >= 1000000000) {
      return (numValue / 1000000000).toFixed(1) + "B";
    } else if (numValue >= 1000000) {
      return (numValue / 1000000).toFixed(1) + "M";
    } else if (numValue >= 1000) {
      return (numValue / 1000).toFixed(1) + "K";
    } else {
      return numValue.toLocaleString();
    }
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const now = new Date();

    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}d ${diffHours % 24}h ago`;
    } else {
      return `${diffHours}h ${diffMin % 60}m ago`;
    }
  };

  // Format percentage changes
  const formatPercentChange = (value) => {
    if (!value && value !== 0) return "-";
    return (value >= 0 ? "+" : "") + value.toFixed(2) + "%";
  };

  // Calculate ratio for progress bars
  const calculateRatio = (a, b) => {
    if (!a || !b || a + b === 0) return 0.5; // Default to 50% if no data
    return a / (a + b);
  };

  // Get time period data
  const getTimePeriodData = (period) => {
    const apiPeriod = timeFrameMap[period] || "24h";

    if (!pairStats) {
      return {
        priceChange: 0,
        buys: 0,
        sells: 0,
        buyVolume: 0,
        sellVolume: 0,
        buyers: 0,
        sellers: 0,
        totalVolume: 0,
      };
    }

    const priceChange = pairStats.pricePercentChange?.[apiPeriod] || 0;

    return {
      priceChange: priceChange,
      buys: pairStats.buys?.[apiPeriod] || 0,
      sells: pairStats.sells?.[apiPeriod] || 0,
      buyVolume: pairStats.buyVolume?.[apiPeriod] || 0,
      sellVolume: pairStats.sellVolume?.[apiPeriod] || 0,
      buyers: pairStats.buyers?.[apiPeriod] || 0,
      sellers: pairStats.sellers?.[apiPeriod] || 0,
      totalVolume: pairStats.totalVolume?.[apiPeriod] || 0,
    };
  };

  // Get token social links
  const getTokenLinks = () => {
    const isSolana = chainId === "solana";

    if (!tokenMetadata) return {};

    if (isSolana) {
      return tokenMetadata.links || {};
    } else {
      return tokenMetadata.links || {};
    }
  };

  // Get market cap or FDV - prioritize DexScreener data, fallback to metadata
  const getMarketCapOrFDV = (type = "fdv") => {
    // First try DexScreener market data (most up-to-date)
    if (marketData?.marketCap && type === "market_cap") {
      return marketData.marketCap;
    }

    // Fallback to metadata
    const isSolana = chainId === "solana";
    if (isSolana) {
      return tokenMetadata?.fullyDilutedValue || marketData?.marketCap || 0;
    } else {
      if (type === "market_cap") {
        return marketData?.marketCap || tokenMetadata?.market_cap || 0;
      } else {
        return (
          tokenMetadata?.fully_diluted_valuation ||
          marketData?.marketCap ||
          tokenMetadata?.market_cap ||
          0
        );
      }
    }
  };

  // Get current time period data
  const currentPeriodData = getTimePeriodData(selectedTimeFrame);

  // New helper function to shorten address
  const shortenAddress = (address) => {
    if (!address) return "";
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  // New helper function to handle copy
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    // Could add toast notification here
  };

  if (!token || !pair) {
    return (
      <div className="p-4 text-dex-text-secondary">No token data available</div>
    );
  }

  // Extract token symbols for the pair
  const quoteToken = (() => {
    const isSolana = chainId === "solana";
    if (isSolana) return "SOL";

    const defaultQuoteTokens = {
      "0x1": "ETH",
      "0x38": "BNB",
      "0x89": "MATIC",
      "0xa86a": "AVAX",
      "0xa": "OP",
      "0xa4b1": "ARB",
      "0x2105": "ETH",
      "0xe708": "ETH",
      "0xfa": "FTM",
      "0x171": "PULSE",
      "0x7e4": "RON",
      solana: "SOL",
    };

    return defaultQuoteTokens[chainId] || "ETH";
  })();

  // Get token price and market info - prioritize DexScreener data
  const usdPrice = marketData?.priceUsd || pairStats?.currentUsdPrice || pair.usdPrice || 0;
  const nativePrice = pairStats?.currentNativePrice || pair.nativePrice || 0;
  const totalLiquidity = marketData?.liquidity || pairStats?.totalLiquidityUsd || pair.liquidityUsd || 0;
  const totalVolume24h = marketData?.volume24h || pairStats?.volume24h || pair.volume24hrUsd || 0;

  // Get market cap from metadata or estimate
  const marketCap = getMarketCapOrFDV();

  // Get token links
  const tokenLinks = getTokenLinks();

  // Get token categories (EVM only)
  const tokenCategories =
    !isSolana && tokenMetadata?.categories ? tokenMetadata.categories : [];

  // Get creation time
  const creationTime =
    pairStats?.pairCreated || tokenMetadata?.created_at || null;

  // Get the block explorer URL
  const getExplorerUrl = (address, type = "address") => {
    const isSolana = chainId === "solana";
    const explorer = blockExplorers[chainId] || "";

    if (!explorer) return "#";

    if (isSolana) {
      return `${explorer}/${type === "token" ? "token" : "account"}/${address}`;
    } else {
      return `${explorer}/${type === "token" ? "token" : "address"}/${address}`;
    }
  };

  // Get total supply and tokens in pair
  const getTokenSupply = () => {
    const isSolana = chainId === "solana";

    if (isSolana) {
      return tokenMetadata?.totalSupplyFormatted || "0";
    } else {
      return tokenMetadata?.total_supply_formatted || "0";
    }
  };

  return (
    <div
      ref={containerRef}
      className="text-dex-text-primary h-full overflow-y-auto text-sm"
    >
      {/* Token Header */}
      <div className="p-3 border-b border-dex-border">
        <div className="flex items-center mb-2">
          <img
            src={
              token.logo ||
              tokenMetadata?.logo ||
              "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIxMDAiIGN5PSIxMDAiIHI9IjEwMCIgZmlsbD0iIzM0Mzk0NyIvPjwvc3ZnPg=="
            }
            alt={token.symbol}
            className="w-8 h-8 mr-2 rounded-full bg-dex-bg-tertiary"
            onError={(e) => {
              e.target.onError = null;
              e.target.src =
                "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIxMDAiIGN5PSIxMDAiIHI9IjEwMCIgZmlsbD0iIzM0Mzk0NyIvPjwvc3ZnPg==";
            }}
          />
          <div>
            <h1 className="text-lg font-bold flex items-center">
              {tokenMetadata?.name || token.name}
              <span className="ml-2 text-sm text-dex-text-secondary">
                ({tokenMetadata?.symbol || token.symbol})
              </span>
            </h1>
            <div className="text-sm text-dex-text-secondary">
              {pair.pairLabel} on {pair.exchangeName}
            </div>
            {/* Pump.fun link for Solana tokens */}
            {isSolana && token?.address && (
              <div className="space-y-1">
                <a
                  href={`https://pump.fun/coin/${token.address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#00d897] hover:text-[#00ffb3] transition-colors"
                >
                  <span>🎰</span>
                  <span className="underline">View on pump.fun</span>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <a
                  href={`https://trade.padre.gg/trade/solana/${token.address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#00d897] hover:text-[#00ffb3] transition-colors"
                >
                  <span>⚡</span>
                  <span className="underline">View on Padre</span>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Token Categories for EVM */}
        {tokenCategories && tokenCategories.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2 mb-2">
            {tokenCategories.slice(0, 3).map((category, index) => (
              <span
                key={index}
                className="px-1.5 py-0.5 text-xs bg-dex-bg-tertiary rounded-full text-dex-text-secondary"
              >
                {category}
              </span>
            ))}
            {tokenCategories.length > 3 && (
              <span className="px-1.5 py-0.5 text-xs bg-dex-bg-tertiary rounded-full text-dex-text-secondary">
                +{tokenCategories.length - 3} more
              </span>
            )}
          </div>
        )}

        <div className="flex space-x-2 mt-3">
          {tokenLinks.website && (
            <a
              href={tokenLinks.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 px-3 py-1.5 bg-dex-bg-tertiary hover:bg-dex-bg-highlight rounded-lg text-sm font-medium text-center"
            >
              Website
            </a>
          )}
          {tokenLinks.twitter && (
            <a
              href={tokenLinks.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 px-3 py-1.5 bg-dex-bg-tertiary hover:bg-dex-bg-highlight rounded-lg text-sm font-medium text-center"
            >
              X Community
            </a>
          )}
          {tokenLinks.telegram && (
            <a
              href={tokenLinks.telegram}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 px-3 py-1.5 bg-dex-bg-tertiary hover:bg-dex-bg-highlight rounded-lg text-sm font-medium text-center"
            >
              Telegram
            </a>
          )}
          {!tokenLinks.telegram && !tokenLinks.discord && (
            <a
              href="#"
              className="flex-1 px-3 py-1.5 bg-dex-bg-tertiary hover:bg-dex-bg-highlight rounded-lg text-sm font-medium text-center"
            >
              Telegram
            </a>
          )}
        </div>
      </div>

      {/* Price Information */}
      <div className="grid grid-cols-2 p-3 border-b border-dex-border">
        <div className="col-span-1">
          <div className="text-xs text-dex-text-secondary mb-0.5">
            PRICE USD
          </div>
          <div className="text-sm font-medium">{formatPrice(usdPrice)}</div>
        </div>
        <div className="col-span-1">
          <div className="text-xs text-dex-text-secondary mb-0.5">
            PRICE {quoteToken}
          </div>
          <div className="text-sm font-medium">
            {formatTokenPrice(nativePrice, quoteToken)}
          </div>
        </div>
      </div>

      {/* Liquidity & Volume */}
      <div className="grid grid-cols-3 p-3 border-b border-dex-border">
        <div className="col-span-1">
          <div className="text-xs text-dex-text-secondary mb-0.5">
            LIQUIDITY
          </div>
          <div className="text-xs font-medium flex items-center">
            ${formatNumber(totalLiquidity)}
          </div>
        </div>
        <div className="col-span-1">
          <div className="text-xs text-dex-text-secondary mb-0.5">FDV</div>
          <div className="text-xs font-medium">
            ${formatNumber(getMarketCapOrFDV("fdv"))}
          </div>
        </div>
        <div className="col-span-1">
          <div className="text-xs text-dex-text-secondary mb-0.5">MKT CAP</div>
          <div className="text-xs font-medium">
            ${formatNumber(getMarketCapOrFDV("market_cap"))}
          </div>
        </div>
      </div>

      {/* Trading Controls - Only for Solana */}
      {chainId === "solana" && (
        <TradingControls
          tokenAddress={token?.address || token?.mint}
          pairAddress={pair?.pairAddress}
          chainId={chainId}
          currentPrice={usdPrice}
          pair={pair}
        />
      )}

      <div className="grid grid-cols-3 border-b border-dex-border">
        {["5m", "1h", "24h"].map((period) => {
          const periodData = getTimePeriodData(period);
          const isActive = selectedTimeFrame === period;
          return (
            <div
              key={period}
              className={`col-span-1 p-2 text-center cursor-pointer ${
                isActive ? "bg-dex-bg-secondary" : ""
              }`}
              onClick={() => handleTimeFrameChange(period)}
            >
              <div className="text-xs text-dex-text-secondary mb-0.5">
                {period}
              </div>
              <div
                className={`text-sm font-medium ${
                  periodData.priceChange >= 0
                    ? "text-dex-green"
                    : "text-dex-red"
                }`}
              >
                {formatPercentChange(periodData.priceChange)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pair Details Section - Shows when scrolled down */}
      <div className="border-b border-dex-border">
        {creationTime && (
          <div className="p-2 border-b border-dex-border flex justify-between items-center">
            <div className="text-xs text-dex-text-secondary">Pair created</div>
            <div className="text-xs">{formatTimeAgo(creationTime)}</div>
          </div>
        )}

        {/* Pooled Tokens */}
        {pair.pair && pair.pair.length > 0 && (
          <>
            {pair.pair.map((pairToken, index) => (
              <div
                key={index}
                className="p-2 border-b border-dex-border flex justify-between items-center"
              >
                <div className="text-xs text-dex-text-secondary">
                  Pooled {pairToken.tokenSymbol}
                </div>
                <div className="text-xs">
                  ${formatNumber(pairToken.liquidityUsd)}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Pair Address with Copy and Explorer Link */}
        <div className="p-2 border-b border-dex-border">
          <div className="flex justify-between items-center">
            <div className="text-xs text-dex-text-secondary">Pair</div>
            <div className="flex items-center space-x-1">
              <span className="font-mono text-xs">
                {shortenAddress(pair.pairAddress)}
              </span>
              <button
                onClick={() => handleCopy(pair.pairAddress)}
                className="px-1.5 py-0.5 text-xs bg-dex-bg-tertiary rounded hover:bg-dex-bg-highlight"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              </button>
              <a
                href={getExplorerUrl(pair.pairAddress, "token")}
                target="_blank"
                rel="noopener noreferrer"
                className="px-1.5 py-0.5 text-xs bg-dex-bg-tertiary rounded hover:bg-dex-bg-highlight"
              >
                EXP
              </a>
            </div>
          </div>
        </div>

        {/* Token Address */}
        <div className="p-2 border-b border-dex-border">
          <div className="flex justify-between items-center">
            <div className="text-xs text-dex-text-secondary">
              {token.symbol}
            </div>
            <div className="flex items-center space-x-1">
              <span className="font-mono text-xs">
                {shortenAddress(token.address)}
              </span>
              <button
                onClick={() => handleCopy(token.address)}
                className="px-1.5 py-0.5 text-xs bg-dex-bg-tertiary rounded hover:bg-dex-bg-highlight"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              </button>
              <a
                href={getExplorerUrl(token.address, "token")}
                target="_blank"
                rel="noopener noreferrer"
                className="px-1.5 py-0.5 text-xs bg-dex-bg-tertiary rounded hover:bg-dex-bg-highlight"
              >
                EXP
              </a>
            </div>
          </div>
        </div>

        {/* Quote Token Address */}
        {pair.pair &&
          pair.pair.find(
            (t) => t.tokenAddress.toLowerCase() !== token.address.toLowerCase()
          ) && (
            <div className="p-2">
              <div className="flex justify-between items-center">
                <div className="text-xs text-dex-text-secondary">
                  {
                    pair.pair.find(
                      (t) =>
                        t.tokenAddress.toLowerCase() !==
                        token.address.toLowerCase()
                    ).tokenSymbol
                  }
                </div>
                <div className="flex items-center space-x-1">
                  <span className="font-mono text-xs">
                    {shortenAddress(
                      pair.pair.find(
                        (t) =>
                          t.tokenAddress.toLowerCase() !==
                          token.address.toLowerCase()
                      ).tokenAddress
                    )}
                  </span>
                  <button
                    onClick={() =>
                      handleCopy(
                        pair.pair.find(
                          (t) =>
                            t.tokenAddress.toLowerCase() !==
                            token.address.toLowerCase()
                        ).tokenAddress
                      )
                    }
                    className="px-1.5 py-0.5 text-xs bg-dex-bg-tertiary rounded hover:bg-dex-bg-highlight"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect
                        x="9"
                        y="9"
                        width="13"
                        height="13"
                        rx="2"
                        ry="2"
                      ></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </button>
                  <a
                    href={getExplorerUrl(
                      pair.pair.find(
                        (t) =>
                          t.tokenAddress.toLowerCase() !==
                          token.address.toLowerCase()
                      ).tokenAddress,
                      "token"
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-1.5 py-0.5 text-xs bg-dex-bg-tertiary rounded hover:bg-dex-bg-highlight"
                  >
                    EXP
                  </a>
                </div>
              </div>
            </div>
          )}
      </div>

      {/* Action Buttons */}
      <div className="p-3">
        {token && token.address && (
        <a
            href={`https://x.com/search?q=${encodeURIComponent(token.address)}`}
          target="_blank"
          rel="noopener noreferrer"
            className="flex justify-center items-center w-full p-2 bg-dex-bg-tertiary hover:bg-dex-bg-highlight rounded-lg text-xs"
        >
          <span className="mr-1">🔍</span>
            <span>Search on X</span>
          </a>
        )}
      </div>
    </div>
  );
};

export default TokenInfo;
